/** MODELS, GOALS, AMCs. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { asClient, goalsOwnedBy } from '../clientScope';
import { isGoalPortfolio } from '../../lib/portfolio';
import {
  GET_ASSET_MODELS, GET_ASSET_MODELS_WITH_FUNDS, GET_ASSET_MODEL_CATALOGUE, GET_ASSET_MODEL_DETAIL,
  GET_ACTIVE_GOALS, GET_ASSET_MODEL_VARIANT_METRICS, GET_COMPLETE_ASSET_MODELS, GET_GOALS, GET_GOAL_OPTIONS,
  GET_AMCS, GET_PORTFOLIOS, GET_PORTFOLIO_GOAL, GET_ASSET_CLASSES_WITH_SEBI, GET_SEBI_CATEGORY_LABELS,
  GET_MODEL_VARIANT_VERSIONS, GET_MODEL_VARIANT_PERFORMANCE,
} from '../queries';
import { UPSERT_ASSET_MODEL_FUNDS } from '../mutations';
import { RECOMPUTE_REALIZED_CHARACTERISTICS } from '../mutations/admin';
import { useBook } from './book';
import { useClients } from './clients';
import { useClientScope } from './firm';
import { EMPTY, mapLimited } from './_shared';

/**
 * The model register — one row per model, carrying everything the list shows.
 *
 * `mine` is everything the platform did not publish. The backend records no
 * owner on a model, only a source, so this means "not KeepInvesting's" rather
 * than "this advisor's".
 */
export function useCompleteAssetModels({ filters = null, limit = 200 } = {}) {
  const { data, loading, error, refetch } = useQuery(GET_COMPLETE_ASSET_MODELS, {
    variables: { limit, filters },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const rows = data?.getCompleteAssetModels ?? EMPTY;
    return {
      rows,
      loading,
      error,
      refetch,
      mine: rows.filter((m) => (m.source || '') !== PLATFORM_MODEL_SOURCE),
    };
  }, [data, loading, error, refetch]);
}

/**
 * Which models hold a given scheme, and at what weight.
 *
 * The backend has no reverse lookup, so the whole register is read once with
 * its variants and matched here. A model is listed if ANY of its variants
 * holds the scheme — the variants differ by share class and SIP minimum, not
 * by strategy, so "the Best variant holds it and the Cheapest does not" is a
 * fact about share classes rather than about whether the model uses the fund.
 * Which variants matched is kept so the row can say so.
 */
export function useModelsHoldingFund(securitySymbol, { skip = false } = {}) {
  const { data, loading, error } = useQuery(GET_ASSET_MODELS_WITH_FUNDS, {
    variables: { limit: 200 },
    skip: skip || !securitySymbol,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  return useMemo(() => {
    const all = data?.getCompleteAssetModels ?? EMPTY;
    const want = String(securitySymbol || '');

    const rows = all
      .map((m) => {
        const hits = (m.variants ?? EMPTY)
          .map((v) => {
            const fund = (v.funds ?? EMPTY)
              .find((f) => String(f.holdingSecuritySymbol || '') === want);
            if (!fund) return null;
            return {
              variantId: v.variantId,
              variantName: v.variantName || `Variant ${v.variantId}`,
              /* Fraction on the wire; the screen decides how to show it. */
              weight: Number(fund.weightWithinAssetModel) || 0,
              securityName: fund.securityName || null,
            };
          })
          .filter(Boolean);

        if (!hits.length) return null;
        const weights = hits.map((h) => h.weight);
        return {
          id: m.id,
          name: m.name,
          category: m.category || null,
          source: m.source || null,
          riskPreference: m.riskPreference || null,
          isActive: m.isActive,
          isListable: m.isListable,
          variants: hits,
          variantCount: (m.variants ?? EMPTY).length,
          minWeight: Math.min(...weights),
          maxWeight: Math.max(...weights),
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.maxWeight - a.maxWeight);

    return {
      rows,
      loading,
      error,
      /** Whether the register itself came back, to tell "none" from "no data". */
      scanned: all.length,
    };
  }, [data, loading, error, securitySymbol]);
}

/**
 * How a model's variants have actually behaved — risk, realized, exposures.
 *
 * Read separately from the model record: it is a measurement, not a target,
 * and only the screen showing those tabs asks for it.
 */
export function useAssetModelVariantMetrics(modelId, { topN = 5 } = {}) {
  const numeric = Number(modelId);
  const { data, loading, error, refetch } = useQuery(GET_ASSET_MODEL_VARIANT_METRICS, {
    variables: { modelId: numeric, topN },
    skip: !Number.isFinite(numeric),
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const rows = data?.getAssetModelImplementationVariants ?? EMPTY;
    return {
      loading,
      error,
      refetch,
      rows,
      /** By variant id, so a chosen variant finds its own measurement. */
      byVariant: new Map(rows.map((r) => [String(r.variantId), r])),
    };
  }, [data, loading, error, refetch]);
}

/**
 * One model with its implementation variants.
 *
 * Separate from the register reads because it is far heavier — a variant
 * carries a couple of hundred look-through holdings — so it is only fetched
 * for the model actually being looked at.
 */
export function useAssetModelDetail(id) {
  const numeric = Number(id);
  const { data, loading, error, refetch } = useQuery(GET_ASSET_MODEL_DETAIL, {
    variables: { id: numeric },
    skip: !Number.isFinite(numeric),
    /* `network-only`, not `cache-first`: this cache runs with `addTypename:
       false` (apollo.js), so nested objects — `allocations`, and now
       `allocations[].sebiCategoryAllocations` — have no normalization key and
       are stored as one opaque blob under `getAssetModelById(id)` rather than
       merged per field. A field added to this query after an earlier, narrower
       read of the same id was already cached can be served back as an empty
       array from that stale blob instead of ever reaching the network for it.
       This is the model-editing screen, where correctness matters more than
       the saved round trip anyway. */
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return useMemo(
    () => ({ loading, error, refetch, model: data?.getAssetModelById ?? null }),
    [data, loading, error, refetch],
  );
}

/**
 * Every model variant as something that can be traded into.
 *
 * A model is bought by its portfolio symbol — `alpha_model_<N>`, `_v<K>` for a
 * variant — so each variant is its own row. Read from the same cached register
 * the policy weights use, so offering models in a lookup costs no extra call.
 */
export function useModelVariants({ skip = false } = {}) {
  const { data, loading, error } = useQuery(GET_ASSET_MODELS_WITH_FUNDS, {
    variables: { limit: 200 },
    skip,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return useMemo(() => ({
    loading,
    error,
    rows: (data?.getCompleteAssetModels ?? EMPTY).flatMap((m) => (m.variants ?? EMPTY)
      .filter((v) => v.securitySymbol)
      .map((v) => ({
        symbol: v.securitySymbol,
        modelId: m.id,
        name: `${m.name}${v.variantName ? ` · ${v.variantName}` : ''}`,
        category: m.category,
        riskPreference: m.riskPreference,
        active: m.isActive !== false,
        fundCount: (v.funds ?? EMPTY).length,
      }))),
  }), [data, loading, error]);
}

/**
 * A portfolio's policy weights, security by security.
 *
 * A goal's policy portfolio follows an asset-model variant, and that variant is
 * itself a portfolio — `alpha_model_584_v1` — whose constituents carry their
 * weight within the model. So the policy weight of a security IS its model
 * weight, read from the register in one cached call (the same one the fund
 * deep-dive's Models tab uses), rather than from a portfolio read per client.
 *
 * `bySymbol` maps security → { pct, name }, weights as PERCENTAGES. Empty when
 * the policy portfolio is not a model variant on the register — the caller
 * falls back to that portfolio's own holdings.
 */
export function usePolicyWeights(policySymbol, { skip = false } = {}) {
  const { data, loading, error } = useQuery(GET_ASSET_MODELS_WITH_FUNDS, {
    variables: { limit: 200 },
    skip: skip || !policySymbol,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  return useMemo(() => {
    const want = String(policySymbol || '');
    let model = null;
    let variant = null;
    (data?.getCompleteAssetModels ?? EMPTY).some((m) => {
      const v = (m.variants ?? EMPTY).find((x) => String(x.securitySymbol || '') === want);
      if (v) { model = m; variant = v; }
      return Boolean(v);
    });
    const bySymbol = new Map((variant?.funds ?? EMPTY)
      .filter((f) => f.holdingSecuritySymbol)
      .map((f) => [f.holdingSecuritySymbol, {
        /* Fractions on the wire, like every model weight. */
        pct: (Number(f.weightWithinAssetModel) || 0) * 100,
        name: f.securityName || f.holdingSecuritySymbol,
      }]));
    return {
      loading,
      error,
      bySymbol,
      label: model ? `${model.name}${variant?.variantName ? ` · ${variant.variantName}` : ''}` : null,
    };
  }, [data, loading, error, policySymbol]);
}

/**
 * Naming a model's constituents by hand, rather than letting the sync pick them.
 *
 * `upsertAssetModelFunds` both adds and updates — a symbol already in the
 * variant has its weight rewritten rather than duplicated — and carries its own
 * removals, so adjusting a model over time needs no second mutation.
 *
 * Writes are per VARIANT: a model's variants differ by share class and SIP
 * minimum, so a constituent named on one says nothing about the others.
 *
 * The model detail is refetched on success, because the editor reads the same
 * record it just wrote and would otherwise show the state before the save.
 */
export function useUpsertModelFunds(modelId) {
  const numeric = Number(modelId);
  const [mutate, { loading: saving }] = useMutation(UPSERT_ASSET_MODEL_FUNDS);
  const [error, setError] = useState(null);

  const save = useCallback(async ({ variantIndex, funds = [], removeFunds = [] }) => {
    setError(null);
    if (!Number.isFinite(numeric)) return null;
    try {
      const { data } = await mutate({
        variables: {
          input: {
            modelId: numeric,
            variants: [{ variantIndex: Number(variantIndex), funds, removeFunds }],
          },
        },
        refetchQueries: [{ query: GET_ASSET_MODEL_DETAIL, variables: { id: numeric } }],
        awaitRefetchQueries: true,
      });
      const r = data?.upsertAssetModelFunds ?? null;
      if (r && !r.success) setError(r.message || 'The model could not be saved');
      return r;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [mutate, numeric]);

  return { save, saving, error, reset: () => setError(null) };
}

/* Names compared ignoring case, punctuation and "&" vs "and". */
const taxonomyKey = (s) => String(s ?? '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '');

/**
 * The asset classes a model can allocate to, each with its SEBI categories —
 * ids, names, codes and the labels to show.
 *
 * Ids and names come from `getAssetClassesWithSebiCategories`, the register
 * every model row points into. A category's label is its display name from
 * the SEBI category enum, matched on the name (the enum's `value` IS the
 * name), falling back to the name. A category's `name` is also what the fund
 * master files funds under, so it is the filter `useFunds({ category })` takes.
 */
export function useModelTaxonomy({ skip = false } = {}) {
  const classesQ = useQuery(GET_ASSET_CLASSES_WITH_SEBI, { skip, fetchPolicy: 'cache-first', errorPolicy: 'all' });
  const labelsQ = useQuery(GET_SEBI_CATEGORY_LABELS, {
    variables: { language: 'en' }, skip, fetchPolicy: 'cache-first', errorPolicy: 'all',
  });

  return useMemo(() => {
    const labelByName = new Map((labelsQ.data?.getSebiMutualFundCategoryEnum ?? EMPTY)
      .filter((e) => e.value)
      .map((e) => [taxonomyKey(e.value), e.displayValue || e.value]));
    const classes = (classesQ.data?.getAssetClassesWithSebiCategories ?? EMPTY).map((ac) => ({
      id: ac.id,
      name: ac.name,
      code: ac.code,
      label: ac.name,
      sebiCategories: (ac.sebiCategories ?? EMPTY)
        .map((c) => ({
          id: c.id,
          name: c.name,
          code: c.code,
          label: labelByName.get(taxonomyKey(c.name)) ?? c.name,
          assetClassId: ac.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    })).sort((a, b) => a.label.localeCompare(b.label));
    const classById = new Map(classes.map((c) => [String(c.id), c]));
    const categoryById = new Map(classes.flatMap((c) => c.sebiCategories).map((c) => [String(c.id), c]));
    return {
      classes,
      classById,
      categoryById,
      loading: classesQ.loading || labelsQ.loading,
      error: classesQ.error,
    };
  }, [classesQ.data, classesQ.loading, classesQ.error, labelsQ.data, labelsQ.loading]);
}

/**
 * A model's implementations (variants) and each one's versions, newest first.
 *
 * `byVariant` maps a variant id to `{ variantId, name, latest, versions }`,
 * where each version carries its `funds` in the shape the constituents editor
 * reads (`holdingSecuritySymbol`, `assetClassId`, `sebiCategoryId`, weights).
 * `latest` is the version flagged `isLatest`, or the highest when none is.
 */
export function useModelVariantVersions(modelId, { skip = false } = {}) {
  const numeric = Number(modelId);
  const { data, loading, error, refetch } = useQuery(GET_MODEL_VARIANT_VERSIONS, {
    variables: { modelId: numeric, topN: 200 },
    skip: skip || !Number.isFinite(numeric),
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  return useMemo(() => {
    const byVariant = new Map();
    (data?.getAssetModelImplementationVariants ?? EMPTY).forEach((row) => {
      const key = String(row.variantId);
      const entry = byVariant.get(key) ?? {
        variantId: row.variantId, name: row.variantName, key: row.variantKey, versions: [],
      };
      entry.name = entry.name || row.variantName;
      entry.versions.push({
        version: row.version,
        isLatest: Boolean(row.isLatest),
        securitySymbol: row.securitySymbol,
        asOf: row.measuredAsOf || row.realizedAsOf || null,
        funds: (row.implementations ?? EMPTY).filter((f) => f.holdingSecuritySymbol),
      });
      byVariant.set(key, entry);
    });
    byVariant.forEach((entry) => {
      entry.versions.sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
      entry.latest = entry.versions.find((v) => v.isLatest) ?? entry.versions[0] ?? null;
    });
    return { byVariant, loading, error, refetch };
  }, [data, loading, error, refetch]);
}

/**
 * Every (variant, version) of a model as measured — and the run that measures it.
 *
 * `rows` are newest version first, then by variant. `recompute` runs
 * `recomputeRealizedCharacteristics` with `persist: true`: no arguments
 * recomputes the whole model, `{ variantIndex, version }` one row. An empty
 * list would match nothing, so a filter is sent only when it names something.
 * The read is refetched afterwards, since the mutation's own refetch list does
 * not carry it.
 */
export function useModelVariantPerformance(modelId, { skip = false } = {}) {
  const numeric = Number(modelId);
  const { data, loading, error, refetch } = useQuery(GET_MODEL_VARIANT_PERFORMANCE, {
    variables: { modelId: numeric, topN: 200, version: 0, includePriceSeries: true },
    skip: skip || !Number.isFinite(numeric),
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const [mutate, { loading: computing }] = useMutation(RECOMPUTE_REALIZED_CHARACTERISTICS);

  const recompute = useCallback(async ({ variantIndex, version } = {}) => {
    const { data: out } = await mutate({
      variables: {
        modelIds: [numeric],
        variantIndexes: variantIndex == null ? null : [Number(variantIndex)],
        versions: version == null ? null : [Number(version)],
        persist: true,
        runAllModels: false,
      },
    });
    await refetch();
    return out?.recomputeRealizedCharacteristics ?? null;
  }, [mutate, refetch, numeric]);

  const rows = useMemo(() => [...(data?.getAssetModelImplementationVariants ?? EMPTY)]
    .sort((a, b) => (b.version ?? 0) - (a.version ?? 0) || (a.variantId ?? 0) - (b.variantId ?? 0)),
  [data]);

  return { rows, loading, error, refetch, recompute, computing };
}

/** Models published by the platform, under the name it publishes them with. */
export const PLATFORM_MODEL_SOURCE = 'KeepInvesting';

/**
 * The model register keyed by where each model came from.
 *
 * Separate from `useAssetModels` because the complete-data resolver answers a
 * type with no `source` on it — this is the only read that can tell a model
 * written here from one the platform shipped.
 */
export function useAssetModelCatalogue() {
  const { data, loading, error, refetch } = useQuery(GET_ASSET_MODEL_CATALOGUE, {
    variables: { limit: 200 },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const rows = data?.getAssetModels ?? EMPTY;
    const mine = rows.filter((m) => (m.source || '') !== PLATFORM_MODEL_SOURCE);
    return {
      loading,
      error,
      refetch,
      rows,
      mine,
      /** Ids written here, for filtering the richer complete-data rows. */
      mineIds: new Set(mine.map((m) => String(m.id))),
    };
  }, [data, loading, error, refetch]);
}

/**
 * `getAssetModelCompleteData` has no per-id filter — it is a flat "first N
 * models" read, in whatever order the backend picks. A caller that needs ONE
 * specific model (rather than the register's own paged view) has to ask for
 * enough rows that the one it wants is actually in the page; `limit` is
 * exposed for exactly that, defaulting to the register's own 200 so nothing
 * that already works changes.
 */
export function useAssetModels({ riskPreference, onlyListed, limit = 200 } = {}) {
  const { data, loading, error, refetch } = useQuery(GET_ASSET_MODELS, {
    /* `onlyListed` is non-null on the resolver: null is rejected outright and
       the whole read comes back empty. `riskPreference` is a nullable String,
       so that one may stay null. */
    variables: { riskPreference: riskPreference ?? null, onlyListed: Boolean(onlyListed), limit },
    fetchPolicy: 'cache-first',
  });
  /* `refetch` is exposed because a model can now be created from the screen
     that lists them — the new one has to appear without a reload. */
  return { loading, error, refetch, models: data?.getAssetModelCompleteData ?? EMPTY };
}

/**
 * Goals.
 *
 * `getGoals` takes no user argument — it answers for whoever the token
 * resolves to. Pass `clientUserId` and the query runs on behalf of that client
 * via `X-Target-User-Id`, which is how an advisor sees a client's plan. Without
 * one it returns the caller's own goals, so the Goals screen instead builds the
 * book-wide view from the portfolios that own each goal.
 */
export function useGoals({ clientUserId } = {}) {
  const { data, loading, error, refetch } = useQuery(GET_GOALS, {
    fetchPolicy: clientUserId ? 'no-cache' : 'cache-first',
    context: asClient(clientUserId),
    errorPolicy: 'all',
  });
  const goals = data?.getGoals?.goals ?? EMPTY;
  const owned = useGoalPortfolios(clientUserId);
  const active = useActivePlans(clientUserId);

  return useMemo(() => {
    const rows = goals.map((g) => withPlan(
      shapeGoal(g, owned.byGoal.get(String(g.goalId))),
      active.byGoal.get(String(g.goalId)),
    ));
    return {
      /* The plan read counts: a list that renders before it lands says "no
         plan" about goals that have one. */
      loading: loading || owned.loading || active.loading,
      error,
      refetch: async (...args) => { const r = await refetch(...args); await active.refetch(); return r; },
      rows,
      summary: data?.getGoals?.summary ?? null,
      behind: rows.filter((r) => r.behind),
      ahead: rows.filter((r) => r.fundedPct != null && r.fundedPct >= 100),
      /** Goals with no committed plan — a different problem from being behind one. */
      unplanned: rows.filter((r) => !r.planned),
    };
  }, [goals, data, owned, active, loading, error, refetch]);
}

/**
 * The committed plan behind each goal, by goal id.
 *
 * Only for one client at a time: `getActiveGoals` answers for whoever the call
 * is made on behalf of, and there is no book-wide form of it.
 */
function useActivePlans(clientUserId) {
  const { data, loading, refetch } = useQuery(GET_ACTIVE_GOALS, {
    skip: !clientUserId,
    fetchPolicy: 'no-cache',
    context: asClient(clientUserId),
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const byGoal = new Map();
    (data?.getActiveGoals?.goals ?? EMPTY).forEach((g) => byGoal.set(String(g.goalId), g));
    return { byGoal, loading, refetch: async () => { if (clientUserId) await refetch(); } };
  }, [data, loading, refetch, clientUserId]);
}

/**
 * A goal row with what its committed plan says.
 *
 * `getGoals` reads the precomputed record, which a plan committed minutes ago
 * has not reached: it reports no target and no model until the nightly job
 * runs. The plan itself is authoritative for those, so where it answers, it
 * wins — and a goal the plan knows IS planned, whatever the progress row says.
 */
function withPlan(row, active) {
  const p = active?.goalParameters ?? null;
  if (!active) return row;
  const target = Number(p?.targetAmount);
  const planned = row.planned || (Number.isFinite(target) && target > 0) || active.recommendedModelId != null;
  return {
    ...row,
    planned,
    /** Everything the plan carries, for a form that reopens on this goal. */
    parameters: p,
    target: row.target || (Number.isFinite(target) ? target : 0),
    monthlyContribution: row.monthlyContribution || Number(p?.monthlyContribution) || 0,
    recommendedModel: row.recommendedModel ?? (active.recommendedModelId == null ? null : {
      assetClassModelId: active.recommendedModelId,
      variantId: active.recommendedModelVariantId,
      modelName: active.recommendedModelStandardName,
      modelStandardName: active.recommendedModelStandardName,
    }),
  };
}

/**
 * A client's portfolios grouped by the goal they belong to.
 *
 * Fetched PER USER, not from the book. `goalId` comes back null on every row of
 * the `allUsers: true` query the rest of the app runs on, and populated when
 * the same portfolios are asked for one user at a time — so this is the only
 * way to tell which portfolio holds which goal's money.
 *
 * A goal has up to three portfolios and they are not interchangeable: the money
 * sits in `<user>_<goal>_goal`, the chosen model's implementation in a
 * `model_portfolio`, and the policy benchmark is not the user's at all and
 * arrives on the goal itself.
 */
export function useGoalPortfolios(clientUserId) {
  const { data, loading, error } = useQuery(GET_PORTFOLIOS, {
    variables: { userId: clientUserId ? String(clientUserId) : null, allUsers: false, limit: 200 },
    skip: !clientUserId,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  return useMemo(() => {
    const rows = data?.getPortfolios?.portfolios ?? EMPTY;
    const byGoal = new Map();
    rows.forEach((p) => {
      if (p.goalId == null) return;
      const key = String(p.goalId);
      const cur = byGoal.get(key) || { money: null, model: null, all: [] };
      cur.all.push(p);
      if (isGoalPortfolio(p)) cur.money = p;
      else if (p.isModel || String(p.portfolioType || '').includes('model')) cur.model = p;
      byGoal.set(key, cur);
    });
    return { loading, error, byGoal };
  }, [data, loading, error]);
}

/**
 * One goal row in the shape every screen renders.
 *
 * `current` falls back to the goal portfolio's own market value when no plan
 * progress exists, and `valueSource` says which was used. Reading only
 * `progress.currentValue` reports zero for every goal on this backend while the
 * portfolios behind them hold real money, because progress rows hang off a
 * committed plan and nothing here has ever committed one.
 *
 * `planned` is that distinction made explicit. A goal with no plan is not a
 * goal behind plan, and the two should never share a badge.
 */
function shapeGoal(g, portfolios) {
  const pr = g.progress ?? {};
  const funded = Number(pr.fundedStatus);
  const tracked = Number(pr.currentValue);
  const held = Number(portfolios?.money?.marketData?.marketValue);
  const hasTracked = Number.isFinite(tracked) && tracked > 0;
  const planned = Boolean(pr.asOfDate) || Number(pr.targetValue) > 0;

  return {
    ...g,
    planned,
    current: hasTracked ? tracked : Number.isFinite(held) ? held : 0,
    valueSource: hasTracked ? 'plan' : Number.isFinite(held) ? 'portfolio' : 'none',
    moneyPortfolio: portfolios?.money ?? null,
    modelPortfolio: portfolios?.model ?? null,
    target: Number(pr.targetValue) || Number(g.goalTarget) || 0,
    fundedPct: Number.isFinite(funded) && planned ? funded * 100 : null,
    /* Behind means measured against a plan and short of it. Without a plan
       there is nothing to be behind, however little the portfolio holds. */
    behind: planned && (pr.shortfallFlag === true || Number(pr.shortfallVsPlan) < 0),
    probability: g.feasibility?.probOfMeetingTarget != null
      ? Number(g.feasibility.probOfMeetingTarget) * 100
      : g.assetAllocation?.successProbability != null
        ? Number(g.assetAllocation.successProbability) * 100
        : null,
  };
}

/**
 * The goals a client's own portfolios hold the money for, read off the
 * portfolios themselves.
 *
 * `getGoals` will not answer for a client (see `useClientGoals`), but a
 * portfolio carries its goal inline and is readable by symbol — verified
 * against the running backend, where an advisor gets a client's `goalData`
 * identically to the client. So the goal's NAME, target, contribution and the
 * money against it are reachable even when the PLAN is not, which is the
 * difference between a screen that lists a client's three goals and one that
 * says it cannot read them.
 *
 * What is NOT derivable is whether a goal is on plan: that is the plan's own
 * arithmetic (shortfall against a schedule), and a funded percentage alone
 * would call a long-dated goal "behind" on day one. These rows are therefore
 * marked `derived` and never counted as behind, so they cannot raise a false
 * action item.
 *
 * One small query per goal portfolio — the client's own list bounds it — driven
 * directly because `useQuery` cannot loop.
 */
function useGoalsFromPortfolios(portfolios) {
  const apollo = useApolloClient();
  const symbols = (portfolios ?? EMPTY)
    .filter((p) => p.goalId != null || isGoalPortfolio(p))
    .map((p) => p.securitySymbol)
    .filter(Boolean);
  const key = symbols.join(',');
  const [state, setState] = useState({ rows: EMPTY, key: null, loading: false });

  useEffect(() => {
    if (!key) { setState({ rows: EMPTY, key: '', loading: false }); return undefined; }
    let current = true;
    setState((s) => ({ ...s, loading: true }));
    mapLimited(key.split(','), (securitySymbol) => apollo
      .query({ query: GET_PORTFOLIO_GOAL, variables: { securitySymbol }, fetchPolicy: 'cache-first', errorPolicy: 'all' })
      .then((res) => res.data?.getPortfolio?.portfolio ?? null)
      .catch(() => null))
      .then((list) => {
        if (!current) return;
        setState({ key, loading: false, rows: list.filter((p) => p?.goalData?.goalId != null) });
      });
    return () => { current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, apollo]);

  return useMemo(() => ({
    loading: state.loading || (Boolean(key) && state.key !== key),
    rows: state.rows.map((p) => {
      const g = p.goalData;
      const current = Number(p.marketData?.marketValue) || 0;
      const target = Number(g.targetAmount) || null;
      return {
        goalId: g.goalId,
        goalName: g.goalName,
        displayName: g.goalName,
        goalType: g.goalType,
        status: g.goalStatus,
        target,
        current,
        fundedPct: target ? (current / target) * 100 : null,
        monthlyContribution: Number(g.monthlyContribution) || null,
        timeHorizon: g.monthsToTarget != null ? Math.round(g.monthsToTarget / 12) : null,
        probability: null,
        /* Planned only if the goal actually carries a plan — a target or a
           contribution. Several of these carry neither (the goal exists, the
           money is against it, nothing was ever sized), and calling that
           "planned" is what makes a screen claim a plan it cannot show. */
        planned: target != null || Number(g.monthlyContribution) > 0,
        /* Never "behind": that is the plan's own arithmetic against a schedule,
           and a funded percentage alone would call a ten-year goal behind on
           its first day — a false action item on every client. */
        behind: false,
        derived: true,
        moneyPortfolio: { securitySymbol: p.securitySymbol, portfolioName: p.portfolioName },
      };
    }),
  }), [state, key]);
}

/**
 * A single client's goals — only the ones that are provably theirs.
 *
 * `getGoals` takes no user argument, so the only way to ask for a client's plan
 * is the `X-Target-User-Id` header, and the backend ignores that header for a
 * firm member: `_is_employee_like` (helper.py:553) asks for an INTERNAL staff
 * employee row, which an external firm's employee is not, so the call resolves
 * to the ADVISOR. Measured against the running backend: for an advisor with an
 * APPROVED link to a client who owns two goals, `getGoals` answers `[]` with
 * the header and without it.
 *
 * So neither list may be shown as the client's on trust. Both are filtered
 * through the goal ids this client's own portfolios point at, which is
 * app_admin's route and is right whichever way the header behaves: a goal the
 * client's portfolio owns is theirs, and one nobody's portfolio owns is not
 * theirs to show. Without that filter the advisor's own goals appeared under
 * the client's name — and picking one blanked every analytic tab, because its
 * portfolio symbol is not in this client's book.
 *
 * `unreadable` is the honest remainder: goal ids this client's portfolios carry
 * that the backend will not hand over. A screen should say the plan cannot be
 * read rather than imply there is none.
 */
export function useClientGoals(client) {
  const scoped = useGoals({ clientUserId: client?.id });
  const own = useGoals();
  const fromPortfolios = useGoalsFromPortfolios(client?.portfolios);
  /* The individual IS the client: every goal the token answers with is
     theirs, portfolio or no portfolio — a goal planned before any money
     arrived must still show. The ownership filter is for an advisor's book. */
  const { self } = useClientScope();

  return useMemo(() => {
    const byId = new Map();
    [...scoped.rows, ...own.rows].forEach((g) => byId.set(String(g.goalId), g));
    const verified = self ? [...byId.values()] : goalsOwnedBy([...byId.values()], client?.portfolios);
    /* A real plan wins over the same goal read off its portfolio. */
    const shownIds = new Set(verified.map((g) => String(g.goalId)));
    const rows = [...verified, ...fromPortfolios.rows.filter((g) => !shownIds.has(String(g.goalId)))];

    /* What this client's portfolios say they have a plan for. */
    const linked = new Set((client?.portfolios ?? EMPTY)
      .flatMap((p) => [p.goalId, p.goalData?.goalId])
      .filter((v) => v != null)
      .map(String));
    const shown = new Set(rows.map((r) => String(r.goalId)));
    const unreadable = [...linked].filter((id) => !shown.has(id));

    return {
      loading: scoped.loading || own.loading || fromPortfolios.loading,
      error: scoped.error || own.error,
      refetch: scoped.refetch,
      rows,
      behind: rows.filter((r) => r.behind),
      ahead: rows.filter((r) => r.fundedPct != null && r.fundedPct >= 100),
      unplanned: rows.filter((r) => !r.planned),
      /** Goal ids the client's portfolios point at that could not be read. */
      unreadable,
      /** True when nothing linked back — the tab should say so, not look empty. */
      unlinked: !self && rows.length === 0 && own.rows.length > 0,
    };
  }, [scoped, own, fromPortfolios, client, self]);
}

/**
 * Book-wide goals, built from the portfolios that own them.
 *
 * Every portfolio carries its goal inline (`goalData`), so this covers the whole
 * client base in the one call the dashboard already makes — no per-client
 * fan-out, and no pretending the caller's own goals are the book.
 */
export function useBookGoals() {
  const book = useBook();
  const clients = useClients();

  return useMemo(() => {
    const nameFor = (userId) => clients.rows.find((c) => c.id === String(userId))?.name || null;
    const byGoal = new Map();

    book.book.forEach((p) => {
      const g = p.goalData;
      if (!g?.goalId) return;
      const key = String(g.goalId);
      const cur = byGoal.get(key) || {
        goalId: g.goalId,
        goalName: g.goalName,
        displayName: g.goalName,
        goalType: g.goalType,
        goalStatus: g.goalStatus,
        target: Number(g.targetAmount) || 0,
        monthlyContribution: Number(g.monthlyContribution) || 0,
        monthsToTarget: g.monthsToTarget ?? null,
        riskTolerance: g.riskTolerance ?? null,
        current: 0,
        accounts: 0,
        clientId: p.userId ? String(p.userId) : null,
        clientName: nameFor(p.userId),
        symbols: [],
      };
      cur.current += Number(p.marketData?.marketValue) || 0;
      cur.accounts += 1;
      cur.symbols.push(p.securitySymbol);
      byGoal.set(key, cur);
    });

    const rows = [...byGoal.values()]
      .map((g) => ({
        ...g,
        fundedPct: g.target ? (g.current / g.target) * 100 : null,
        behind: g.target ? g.current / g.target < 1 : false,
        probability: null,
      }))
      .sort((a, b) => b.current - a.current);

    return {
      loading: book.loading || clients.loading,
      error: book.error || clients.error,
      rows,
      behind: rows.filter((r) => r.behind),
      ahead: rows.filter((r) => r.fundedPct != null && r.fundedPct >= 100),
    };
  }, [book, clients]);
}

export function useAmcs() {
  const { data, loading, error } = useQuery(GET_AMCS, {
    variables: { empanelledOnly: false },
    fetchPolicy: 'cache-first',
  });
  const rows = data?.getAmcs ?? EMPTY;
  return {
    loading,
    error,
    rows,
    empanelled: rows.filter((a) => a.isEmpanelled).length,
  };
}

/**
 * Model-level trades implied by the average gap between book and model
 * weights — what Mandates shows as the rebalance the model would ask for.
 */
export function useRebalanceLegs(model, book) {
  return useMemo(() => {
    if (!model) return EMPTY;
    const target = new Map(
      (model.broadAllocations ?? EMPTY).map((a) => [a.assetClassName, Number(a.weight) || 0]),
    );
    const actual = new Map();
    let total = 0;
    book.book.forEach((p) => {
      const v = Number(p.marketData?.marketValue) || 0;
      total += v;
      const key = (p.portfolioType || 'OTHER').replace(/_/g, ' ');
      actual.set(key, (actual.get(key) || 0) + v);
    });
    return [...target.entries()].map(([name, w]) => {
      const cur = total ? (actual.get(name) || 0) / total : 0;
      const gap = w - cur;
      return {
        name,
        target: w * 100,
        current: cur * 100,
        gap: gap * 100,
        turnover: Math.abs(gap) * total,
        accounts: book.book.length,
      };
    });
  }, [model, book.book]);
}

/**
 * The goal planner's choices, as `{ value, label }` lists keyed by the
 * `source` a field in `config/goalTypes.js` names.
 *
 * Loan types keep the app's eligibility rule: only EMI debts that normally run
 * five years or longer. A paydown plan needs at least three years of runway to
 * beat the EMI, and the shorter debts rarely have it. The typical rate band
 * travels with each option so the form can say what is normal for that loan.
 */
export function useGoalOptions({ skip = false } = {}) {
  const { data, loading, error } = useQuery(GET_GOAL_OPTIONS, {
    skip,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const list = (rows) => (rows ?? EMPTY).map((r) => ({ value: r.value, label: r.displayValue || r.value }));
    const loans = (data?.loanTypes?.loanTypes ?? EMPTY)
      .filter((l) => l.isEmi && Number(l.typicalTenureYearsMin) >= 5)
      .map((l) => ({
        value: l.key,
        label: l.displayName || l.key,
        rateMin: l.typicalRateMinPct,
        rateMax: l.typicalRateMaxPct,
      }));
    return {
      loading,
      error,
      options: {
        risk: list(data?.riskProfiles),
        program: list(data?.programTypes),
        property: list(data?.propertyTypes),
        frequency: list(data?.frequencies),
        loan: loans,
      },
    };
  }, [data, loading, error]);
}
